import { ReactNode } from 'react';
import styled from 'styled-components';
import {
  APISurveyAttributes,
  ArdoqId,
  PersistedReferenceSubQuestion,
} from '@ardoq/api-types';
import { SelectOption } from '@ardoq/select';
import { Locale } from '@ardoq/locale';
import { Text } from '@ardoq/typography';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import {
  ReferenceAnswerValue,
  Fields,
  AdditionalFilterOptionsFunc,
  GetAISuggestions,
  FetchComponentOptionsWithQuery,
  GenerateComponentDescription,
} from '../../types';
import { SurveyReadOnlyQuestion } from '../SurveyReadOnlyQuestion';
import { SurveyReferenceQuestionWithQuestions } from './SurveyReferenceQuestionWithQuestions';
import { SetNestedAnswerPayload } from '../ReferenceQuestions/referenceTypes';
import { QuestionContentWrapper } from '../questionsTypes';
import { FlexBox, Stack } from '@ardoq/layout';
import { getReferenceId } from '../../utils/answerOperations';

const References = styled(Stack)`
  &:not(:empty) {
    padding: 24px;
  }
`;

type ReadonlyReferenceLabelProps = {
  hideReadOnlyLabel: boolean;
  references: ReferenceAnswerValue[];
  i18nLocale?: I18nLocale;
};

export const SurveyReadonlyReferenceLabel = ({
  hideReadOnlyLabel,
  references,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: ReadonlyReferenceLabelProps) => {
  return !references.length ? (
    <SurveyReadOnlyQuestion
      hideReadOnlyLabel={hideReadOnlyLabel}
      i18nLocale={i18nLocale}
    >
      {t(i18nLocale, 'surveysUi:noneSelected', 'None selected.')}
    </SurveyReadOnlyQuestion>
  ) : (
    <Text variant="caption" color="textSubtle">
      {t(i18nLocale, 'surveysUi:seeEntriesBelow', 'See entries below:')}
    </Text>
  );
};

type SurveyListReferencesWithQuestionsProps = {
  survey: APISurveyAttributes;
  parentId: string;
  references: ReferenceAnswerValue[];
  questionId: string;
  questionLabel: string;
  componentId: ArdoqId | undefined;
  questions?: PersistedReferenceSubQuestion[];
  readonly: boolean;
  input?: ReactNode;
  fields: Fields;
  preventReferenceModification?: boolean | null;
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  hasAttemptedSubmission: boolean;
  identifierFieldName: string | undefined;
  hasAutocompleteFeature: boolean;
  hasEditableReferencesInQuestionsFeature: boolean;
  isEmbedded: boolean;
  hideReadOnlyLabel: boolean;
  QuestionContentWrapper: QuestionContentWrapper;
  allowComponentCreation: boolean;
  highlightUnansweredPriorityQuestions: boolean;
  generateComponentDescription: GenerateComponentDescription;
  trackDescriptionGenerated: (value: string | null | undefined) => () => void;
  trackDescriptionGeneratedFailed: () => void;
  additionalFilterOptions?: AdditionalFilterOptionsFunc;
  getDuplicate: (value: string) => null | { path: string };
  getExistingComponentByName: (name: string) => void;
  deleteReference: (ref: ReferenceAnswerValue) => void;
  editReference: (
    ref: ReferenceAnswerValue,
    componentOption: SelectOption<string>
  ) => void;
  setNestedAnswer: (answer: SetNestedAnswerPayload) => void;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
  userOptions: SelectOption<ArdoqId>[];
};

export const SurveyListReferencesWithQuestions = ({
  survey,
  references,
  questionId,
  componentId,
  questionLabel,
  questions,
  readonly,
  parentId,
  input,
  fields,
  preventReferenceModification,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  hasAttemptedSubmission,
  identifierFieldName,
  hasAutocompleteFeature,
  hasEditableReferencesInQuestionsFeature,
  isEmbedded,
  hideReadOnlyLabel,
  QuestionContentWrapper,
  allowComponentCreation,
  highlightUnansweredPriorityQuestions,
  generateComponentDescription,
  trackDescriptionGenerated,
  trackDescriptionGeneratedFailed,
  additionalFilterOptions,
  getDuplicate,
  getExistingComponentByName,
  deleteReference,
  editReference,
  setNestedAnswer,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  userOptions,
}: SurveyListReferencesWithQuestionsProps) => {
  return (
    <FlexBox width="full">
      <Stack flex={1}>
        {readonly || preventReferenceModification ? (
          <SurveyReadonlyReferenceLabel
            hideReadOnlyLabel={hideReadOnlyLabel}
            references={references}
            i18nLocale={i18nLocale}
          />
        ) : (
          input
        )}
        <References gap="small">
          {references.map(ref => {
            const id = getReferenceId(ref);
            const identifier = ref.tempId ? 'tempId' : 'referenceId';
            return (
              <SurveyReferenceQuestionWithQuestions
                userOptions={userOptions}
                fields={fields}
                survey={survey}
                answers={ref.answers}
                initialCollapsed={!ref.tempId}
                key={id}
                questions={questions}
                readonly={readonly}
                reference={ref}
                questionId={questionId}
                questionLabel={questionLabel}
                componentId={componentId}
                preventReferenceModification={preventReferenceModification}
                currentLocale={currentLocale}
                i18nLocale={i18nLocale}
                hasAttemptedSubmission={hasAttemptedSubmission}
                identifierFieldName={identifierFieldName}
                hasAutocompleteFeature={hasAutocompleteFeature}
                hasEditableReferencesInQuestionsFeature={
                  hasEditableReferencesInQuestionsFeature
                }
                isEmbedded={isEmbedded}
                hideReadOnlyLabel={hideReadOnlyLabel}
                QuestionContentWrapper={QuestionContentWrapper}
                allowComponentCreation={allowComponentCreation}
                highlightUnansweredPriorityQuestions={
                  highlightUnansweredPriorityQuestions
                }
                setAnswer={({ questionId, value }) =>
                  setNestedAnswer({
                    parentId,
                    id,
                    identifier,
                    questionId,
                    value,
                  })
                }
                deleteReference={() => deleteReference(ref)}
                editReference={componentOption => {
                  editReference(ref, componentOption);
                }}
                additionalFilterOptions={additionalFilterOptions}
                getDuplicate={getDuplicate}
                getExistingComponentByName={getExistingComponentByName}
                generateComponentDescription={generateComponentDescription}
                trackDescriptionGenerated={trackDescriptionGenerated}
                trackDescriptionGeneratedFailed={
                  trackDescriptionGeneratedFailed
                }
                getAISuggestions={getAISuggestions}
                fetchComponentOptionsWithQuery={fetchComponentOptionsWithQuery}
              />
            );
          })}
        </References>
      </Stack>
    </FlexBox>
  );
};
